import { useAppTranslation } from "@/utils";
import { Flex, Select, Tag, Typography } from "@payping/web-components";
import { Outlet, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import ListItem from "./ItemList";
import FeaturedItem from "./FeaturedItem";
import { featuredPluginsList, pluginsListList, pluginsTagsList } from "./data";
import GetTokenModal from "./GetTokenModal";

const ConnectionsPluginsPage = () => {
  const { t } = useAppTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchList = searchParams.getAll("searchList");
  const [openModal, setOpenModal] = useState<string>();

  const filteredPluginsList = useMemo(() => {
    let objectTemp: typeof pluginsListList = {};

    if (!searchList.length) return pluginsListList;

    Object.entries(pluginsListList).forEach(([key, value]) => {
      let currentScore = 0;
      const validScore = searchList.length;

      searchList.forEach((item) => {
        // searchValue is plugin title
        if (item.startsWith("plugin_")) {
          if (value.translationKey.includes(item.replace("plugin_", ""))) {
            currentScore += 1;
            return;
          }
        }
        // search value is tag
        if (value.tags?.includes(item as (typeof pluginsTagsList)[keyof typeof pluginsTagsList])) {
          currentScore += 1;
        }
      });

      if (currentScore === validScore) {
        objectTemp = { ...objectTemp, [key]: value };
      }
    });

    return objectTemp;
  }, [searchList]);

  const addTag = (item: string) => {
    setSearchParams((pre) => {
      const tags = pre.getAll("searchList").includes(item)
        ? pre.getAll("searchList").filter((i) => i !== item)
        : [...pre.getAll("searchList"), item];
      pre.delete("searchList");
      tags.forEach((i) => {
        pre.append("searchList", i);
      });

      return pre;
    });
  };

  const searchOptions = (): { value: string; label: string; title: string }[] => {
    const options: { value: string; label: string; title: string }[] = [];

    Object.values(pluginsTagsList).forEach((item) => {
      options.push({
        value: item,
        label: t(`connections.plugins.tags.${item}`),
        title: t(`connections.plugins.tags.${item}`),
      });
    });

    Object.values(pluginsListList).forEach((item) => {
      options.push({
        value: `plugin_${item.translationKey}`,
        label: t(`connections.plugins.list.${item.translationKey}.title`),
        title: t(`connections.plugins.list.${item.translationKey}.title`),
      });
    });

    return options;
  };

  const onChangeSearch = (value: string[]) => {
    setSearchParams((pre) => {
      pre.delete("searchList");
      value.forEach((i) => {
        pre.append("searchList", i);
      });
      return pre;
    });
  };

  return (
    <>
      <Flex className="tw:w-full" gap="2rem" vertical justify="center">
        <Flex wrap="wrap" gap="1rem">
          {Object.entries(featuredPluginsList).map(([, value]) => (
            <FeaturedItem key={value.translationKey} data={value} googleTrackId={value.googleTrackId} />
          ))}
        </Flex>
        <Typography.Title className="tw:mb-0! tw:w-full" level={3}>
          {t("connections.plugins.title")}
        </Typography.Title>
        <Flex vertical gap="0.5rem">
          <div className="tw:w-max-[28rem] tw:w-full">
            <Select
              value={searchList}
              className="tw:w-full"
              showSearch
              allowClear
              options={searchOptions()}
              mode="multiple"
              onChange={onChangeSearch}
              filterOption={(input, options) => {
                if (
                  options?.label.includes(input.trim()) ||
                  options?.title.includes(input.trim()) ||
                  options?.value.includes(input.trim())
                ) {
                  return true;
                }
                return false;
              }}
            />
          </div>
          <Flex gap="0.5rem" wrap="wrap">
            <Tag
              onClick={() => {
                addTag("wordpress");
              }}
              key={"wordpress"}
              className="tw:cursor-pointer"
            >
              {t(`connections.plugins.tags.wordpress`)}
            </Tag>
            <Tag
              onClick={() => {
                addTag("wooCommerce");
              }}
              key={"wooCommerce"}
              className="tw:cursor-pointer"
            >
              {t(`connections.plugins.tags.wooCommerce`)}
            </Tag>
            <Tag
              onClick={() => {
                addTag("eCommerce");
              }}
              key={"eCommerce"}
              className="tw:cursor-pointer"
            >
              {t(`connections.plugins.tags.eCommerce`)}
            </Tag>
            <Tag
              onClick={() => {
                addTag("donate");
              }}
              key={"donate"}
              className="tw:cursor-pointer"
            >
              {t(`connections.plugins.tags.donate`)}
            </Tag>
          </Flex>
        </Flex>
        <Flex gap="0.5rem" vertical>
          {Object.entries(filteredPluginsList).map(([, value]) => (
            <ListItem
              key={value.translationKey}
              data={value}
              addTag={addTag}
              clickToGetToken={() => setOpenModal(value.translationKey)}
            />
          ))}
        </Flex>
      </Flex>
      {openModal ? (
        <GetTokenModal onClose={() => setOpenModal(undefined)} visible={!!openModal} pluginKey={openModal} />
      ) : null}
      <Outlet />
    </>
  );
};

export default ConnectionsPluginsPage;
